/**
 * Reading a token's claims, without the sign-in library (plan 45 phase 2: the
 * portals render the first page before Amplify loads).
 */

import { mfaPerformed } from '@laura/shared/permissions';

/** Claims the UI reads. Never trusted for authorisation: the API decides (hard rule 1). */
export interface Claims {
  sub: string;
  role: string;
  tenantId?: string;
  /** Plan 257 EF-08: true only when this sign-in used the factor (`mfaPerformed`). */
  mfa?: boolean;
  /**
   * Plan 257 EF-08: a factor is enrolled, whether or not this sign-in used it. True
   * with `mfa` false means "sign in again and enter the code".
   */
  mfaEnrolled?: boolean;
  /** ADR 242: a clinic-pool token with no clinic (the person picks one, or has none). */
  personal?: boolean;
  authTime: number;
  expires: number;
}

function payload(token: string): Record<string, unknown> {
  const part = token.split('.')[1];
  if (!part) throw new Error('Not a JWT');
  return JSON.parse(
    new TextDecoder().decode(
      Uint8Array.from(atob(part.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0)),
    ),
  ) as Record<string, unknown>;
}

export function readClaims(token: string): Claims {
  const json = payload(token);
  return {
    sub: String(json.sub),
    role: String(json.role ?? ''),
    ...(typeof json.tenant_id === 'string' && { tenantId: json.tenant_id }),
    ...(typeof json.mfa === 'string' && {
      mfa: mfaPerformed(json),
      mfaEnrolled: json.mfa === 'true',
    }),
    ...(json.personal === 'true' && { personal: true }),
    authTime: Number(json.auth_time ?? 0),
    expires: Number(json.exp ?? 0),
  };
}
